import { kCreateOrganization } from '@/constants/constants-api-paths'
import { kPaxtonAppApiRESTBaseUrl } from '@/constants/constants-links'
import { getAuth } from 'firebase/auth'
import { nanoid } from 'nanoid'
import * as Sentry from '@sentry/browser'
import { CreateOrganizationResponseSchema } from '../schemas'
import { ApiError } from '@/util/errors/api-error'
import { CreateSelfServeOrganizationRequest, CreateOrganizationResponse } from '../schemas'

/**
 * Post Create Organization
 *
 * Calls the backend endpoint to create a new organization for the current user.
 *
 * @param createOrganizationRequest - Data for the new organization
 * @returns CreateOrganizationResponse
 */
export async function postCreateOrganization(createOrganizationRequest: CreateSelfServeOrganizationRequest): Promise<CreateOrganizationResponse> {
  const sentryTransactionId = nanoid()

  try {
    const user = getAuth().currentUser
    if (!user) throw new Error('User not authenticated.')
    const token = await user.getIdToken()
    if (!token) throw new Error("Could not retrieve user's authentication token.")

    const requestOptions: RequestInit = {
      headers: {
        'Content-Type': 'application/json; charset=UTF-8',
        'X-Transaction-Id': sentryTransactionId,
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(createOrganizationRequest),
      method: 'POST',
    }

    const apiUrl = `${kPaxtonAppApiRESTBaseUrl()}${kCreateOrganization}`
    const response = await fetch(apiUrl, requestOptions)
    const data = await response.json()

    if (!response.ok) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentryTransactionId })
        Sentry.captureException(new Error(`Fetch error - status: ${response.status}`), {
          extra: {
            requestUrl: apiUrl,
            requestOptions,
            response: JSON.stringify(response),
            data: JSON.stringify(data),
            onLine: navigator.onLine,
            cookieEnabled: navigator.cookieEnabled,
            createOrganizationRequest: JSON.stringify(createOrganizationRequest),
          },
        })
      })

      throw new ApiError(response.status, `Fetch error - status: ${response.status}, detail: ${JSON.stringify(data.detail)}`)
    }

    return CreateOrganizationResponseSchema.parse(data)
  } catch (error) {
    // Skip api errors already captured above
    if (!(error instanceof ApiError)) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentryTransactionId })
        Sentry.captureException(error, {
          extra: { onLine: navigator.onLine, cookieEnabled: navigator.cookieEnabled, organizationData: JSON.stringify(createOrganizationRequest) },
        })
      })
    }
    throw error
  }
}
